import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "../services/api";
import { todayLocalISO } from "../utils/date";
import styles from "./BodyProgressModal.module.css";
import PhotoGuideModal from "./PhotoGuideModal";
import { getCssVar } from "../utils/theme";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useToast } from "./ToastProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faScaleBalanced, faCamera, faBrain, faSpinner, faLock, faClockRotateLeft, faChevronDown, faChevronUp, faArrowsRotate, faChartBar } from "@fortawesome/free-solid-svg-icons";

const ANGLES = [
  { key: "front", label: "Frente" },
  { key: "back",  label: "Costas" },
  { key: "right", label: "Lado Dir." },
  { key: "left",  label: "Lado Esq." },
];

function buildSrc(path, apiBase) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${apiBase || ""}/${path}`.replace(/([^:]\/)\/+/g, "$1");
}

function normalizePath(url) {
  if (!url) return "";
  const path = url.startsWith("http")
    ? url.replace(/^https?:\/\/[^/]+/, "").replace(/^\//, "")
    : url.replace(/^\//, "");
  return path.startsWith("uploads/") ? path : `uploads/${path}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + (iso.includes("T") ? "" : "T00:00:00"));
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDatePtBR(iso) {
  if (!iso) return "";
  const d = new Date(iso + (iso.includes("T") ? "" : "T00:00:00"));
  return d.toLocaleDateString("pt-BR");
}

// ── Album card ────────────────────────────────────────────────────────────────
function AlbumCard({ entry, apiBase, onClick, onDelete, onEdit, isProtected }) {
  const photos = [
    { key: "front", url: entry.photo_front_url, label: "Frente" },
    { key: "back",  url: entry.photo_back_url,  label: "Costas" },
    { key: "right", url: entry.photo_right_url, label: "Dir." },
    { key: "left",  url: entry.photo_left_url,  label: "Esq." },
  ];
  const hasAny = photos.some(p => p.url);

  return (
    <div className={styles.card}>
      {/* Área clicável para abrir lightbox (exclui cardMeta) */}
      {hasAny && (
        <div className={styles.album} onClick={onClick} style={{ cursor: "pointer" }}>
          {photos.map(p => (
            <div
              key={p.key}
              className={`${styles.albumThumb} ${!p.url ? styles.albumThumbEmpty : ""}`}
            >
              {p.url && (
                <>
                  <img src={buildSrc(p.url, apiBase)} alt={p.label} loading="lazy" />
                  <span className={styles.albumLabel}>{p.label}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div className={styles.cardMeta}>
        <span
          className={styles.cardDate}
          onClick={onClick}
          style={{ cursor: "pointer", flex: 1 }}
        >{formatDate(entry.recorded_at)}</span>
        <span className={styles.cardWeight}>{entry.weight_kg} kg</span>
        {onEdit && (
          isProtected
            ? <span
                className={styles.cardLockBadge}
                title="Registro analisado pela IA - bloqueado para edição"
              >
                <FontAwesomeIcon icon={faLock} />
              </span>
            : <button
                className={styles.cardEditBtn}
                title="Editar"
                onClick={() => onEdit(entry)}
              >Editar</button>
        )}
        {!isProtected && (
          <button
            className={styles.cardDeleteBtn}
            title="Excluir"
            onClick={() => onDelete(entry)}
          >Excluir</button>
        )}
      </div>

    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ entry, apiBase, onClose }) {
  const photos = [
    { key: "front", url: entry.photo_front_url, label: "Frente" },
    { key: "back",  url: entry.photo_back_url,  label: "Costas" },
    { key: "right", url: entry.photo_right_url, label: "Lado Dir." },
    { key: "left",  url: entry.photo_left_url,  label: "Lado Esq." },
  ];
  return (
    <div className={styles.lightbox} onClick={onClose}>
      <div className={styles.lightboxHeader} onClick={e => e.stopPropagation()}>
        <span className={styles.lightboxDate}>{formatDate(entry.recorded_at)}</span>
        <span className={styles.lightboxWeight}>{entry.weight_kg} kg</span>
        <button className={styles.lightboxClose} onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
      </div>
      <div className={styles.lightboxGrid} onClick={e => e.stopPropagation()}>
        {photos.map(p => (
          <div key={p.key} className={styles.lightboxItem}>
            <span className={styles.lightboxLabel}>{p.label}</span>
            {p.url
              ? <img src={buildSrc(p.url, apiBase)} alt={p.label} className={styles.lightboxPhoto} loading="lazy" />
              : <div className={styles.lightboxEmpty}>-</div>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Inner content (reutilizável standalone ou embedded) ───────────────────────
// studentId: quando definido, carrega fotos do aluno (visão professor) e oculta formulário
export function BodyProgressContent({ apiBase = "", studentId = null, canManage = false, gender: genderProp = null, onAnalyzingChange = null }) {
  // Por padrão, ver dados de um aluno (studentId setado) é read-only — usado
  // em TeacherAnalyticsModal, onde o professor só acompanha. canManage=true
  // (fluxo "Medidas corporais" do professor, gerenciando o aluno) libera
  // criar/editar/apagar registros do aluno, igual ao já suportado no backend.
  const readOnly = !!studentId && !canManage;

  const gender = genderProp ?? (() => {
    try { return JSON.parse(localStorage.getItem("user"))?.gender || ""; } catch { return ""; }
  })();
  const photoGuideFile = gender === "feminino" || gender === "F"
    ? "guia-fotos-progresso-fem.jpg"
    : "guia-fotos-progresso-masc.jpg";
  const photoGuideUrl = `${apiBase}/uploads/${photoGuideFile}`;
  const [photoGuideOpen, setPhotoGuideOpen] = useState(false);

  useBodyScrollLock();
  const toast = useToast();
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editExistingPhotos, setEditExistingPhotos] = useState(null);
  const [lightboxEntry, setLightboxEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [generating, setGenerating] = useState(false);
  const [anglePicker, setAnglePicker] = useState(null); // null | { older, newer, angles }
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalyzeConfirm, setShowAnalyzeConfirm] = useState(false);
  const [evolutionAnalysis, setEvolutionAnalysis] = useState(undefined); // most recent — for isProtected check

  // Helper: is a given entry locked by an AI analysis?
  const entryIsProtected = (entry) =>
    evolutionAnalysis != null && entry != null && (
      entry.id === evolutionAnalysis.newest_entry_id ||
      entry.id === evolutionAnalysis.oldest_entry_id
    );

  // For use inside the edit form (where editEntry is the target)
  const isProtected = entryIsProtected(editEntry);
  const [showHistory, setShowHistory] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState(null); // null=not loaded
  const [expandedAnalysis, setExpandedAnalysis] = useState(null); // id of expanded card

  const [weight, setWeight] = useState("");
  const [recordedAt, setRecordedAt] = useState(() => todayLocalISO());
  const [photos, setPhotos] = useState({ front: null, back: null, right: null, left: null });
  const [previews, setPreviews] = useState({ front: null, back: null, right: null, left: null });
  const [deletePhotos, setDeletePhotos] = useState({ front: false, back: false, right: false, left: false });
  const fileRefs = {
    front: useRef(), back: useRef(), right: useRef(), left: useRef(),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = studentId ? `/body-progress/student/${studentId}` : "/body-progress";
      const res = await api.get(endpoint);
      setEntries(res.data || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (entries === null && !loading) load();
  }, [entries, loading, load]);

  // Load existing evolution analysis on mount
  useEffect(() => {
    if (evolutionAnalysis !== undefined) return;
    const qp = studentId ? `?student_id=${studentId}` : "";
    api.get(`/body-progress/analyze-evolution${qp}`)
      .then(res => setEvolutionAnalysis(res.data))
      .catch(() => setEvolutionAnalysis(null));
  }, [evolutionAnalysis, studentId]);

  const handleAnalyzeEvolution = async () => {
    setAnalyzing(true);
    onAnalyzingChange?.(true);
    setError("");
    try {
      const qp = studentId ? `?student_id=${studentId}` : "";
      const res = await api.post(`/body-progress/analyze-evolution${qp}`);
      setEvolutionAnalysis(res.data);
      // Reload history, open panel, expand newest
      const hres = await api.get(`/body-progress/analyze-evolution/history${qp}`);
      const hist = hres.data || [];
      setAnalysisHistory(hist);
      setShowHistory(true);
      if (hist.length > 0) setExpandedAnalysis(hist[0].id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Erro ao gerar análise.";
      if (e?.response?.status === 429) {
        toast.warning(msg, 6000);
      } else {
        toast.error(msg, 5000);
      }
    } finally {
      setAnalyzing(false);
      onAnalyzingChange?.(false);
    }
  };

  const loadHistory = async () => {
    try {
      const qp = studentId ? `?student_id=${studentId}` : "";
      const res = await api.get(`/body-progress/analyze-evolution/history${qp}`);
      setAnalysisHistory(res.data || []);
    } catch {
      setAnalysisHistory([]);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && analysisHistory === null) loadHistory();
    setShowHistory(h => !h);
  };

  const handlePhoto = (angle, file) => {
    if (!file) return;
    setPhotos(p => ({ ...p, [angle]: file }));
    setPreviews(p => ({ ...p, [angle]: URL.createObjectURL(file) }));
  };

  const removePhoto = (angle) => {
    setPhotos(p => ({ ...p, [angle]: null }));
    setPreviews(p => ({ ...p, [angle]: null }));
  };

  const resetForm = () => {
    setWeight("");
    setRecordedAt(todayLocalISO());
    setPhotos({ front: null, back: null, right: null, left: null });
    setPreviews({ front: null, back: null, right: null, left: null });
    setDeletePhotos({ front: false, back: false, right: false, left: false });
    setEditEntry(null);
    setEditExistingPhotos(null);
    setError("");
  };

  const startEdit = (entry) => {
    setEditEntry(entry);
    setEditExistingPhotos({
      front: entry.photo_front_url || null,
      back:  entry.photo_back_url  || null,
      right: entry.photo_right_url || null,
      left:  entry.photo_left_url  || null,
    });
    setWeight(String(entry.weight_kg));
    setRecordedAt(entry.recorded_at.slice(0, 10));
    setPhotos({ front: null, back: null, right: null, left: null });
    setPreviews({ front: null, back: null, right: null, left: null });
    setError("");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!weight || isNaN(Number(weight)) || Number(weight) <= 0) {
      setError("Informe o peso corretamente.");
      return;
    }
    if (!editEntry) {
      // create: require at least 1 photo
      const photoCount = [photos.front, photos.back, photos.right, photos.left].filter(Boolean).length;
      if (photoCount < 1) {
        setError("Envie ao menos uma foto.");
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("weight_kg", Number(weight));
      fd.append("recorded_at", recordedAt);
      if (studentId) fd.append("student_id", studentId);
      if (photos.front) fd.append("photo_front", photos.front);
      if (photos.back)  fd.append("photo_back",  photos.back);
      if (photos.right) fd.append("photo_right", photos.right);
      if (photos.left)  fd.append("photo_left",  photos.left);

      if (editEntry) {
        if (deletePhotos.front) fd.append("remove_front", "1");
        if (deletePhotos.back)  fd.append("remove_back",  "1");
        if (deletePhotos.right) fd.append("remove_right", "1");
        if (deletePhotos.left)  fd.append("remove_left",  "1");
        const res = await api.put(`/body-progress/${editEntry.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setEntries(prev => (prev || []).map(e => e.id === editEntry.id ? res.data : e));
      } else {
        const res = await api.post("/body-progress", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setEntries(prev => [res.data, ...(prev || [])]);
      }
      resetForm();
      setShowForm(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await api.delete(`/body-progress/${deleteTarget.id}`);
      setEntries(prev => prev.filter(e => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Erro ao excluir. Tente novamente.");
    }
  };

  // ── Before/After — auto compare oldest vs newest, com seleção de ângulo ──
  const generateBeforeAfter = async (angle = null) => {
    const withPhotos = (entries || [])
      .filter(e => e.photo_front_url || e.photo_back_url || e.photo_right_url || e.photo_left_url)
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));

    if (withPhotos.length < 2) {
      toast.error("São necessárias ao menos 2 entradas com fotos para comparação.");
      return;
    }

    const older = withPhotos[0];
    const newer = withPhotos[withPhotos.length - 1];

    // Ângulos disponíveis em AMBAS as entradas
    const commonAngles = ANGLES.filter(
      a => older[`photo_${a.key}_url`] && newer[`photo_${a.key}_url`]
    );

    if (commonAngles.length === 0) {
      toast.error("Não há ângulos em comum entre a primeira e última entrada.");
      return;
    }

    // Se ângulo não escolhido ainda: pedir escolha (ou auto-selecionar se único)
    if (!angle) {
      if (commonAngles.length === 1) {
        angle = commonAngles[0].key;
      } else {
        setAnglePicker({ older, newer, angles: commonAngles });
        return;
      }
    }

    const oldPath = normalizePath(older[`photo_${angle}_url`]);
    const newPath = normalizePath(newer[`photo_${angle}_url`]);
    const oldDate = formatDatePtBR(older.recorded_at);
    const newDate = formatDatePtBR(newer.recorded_at);

    setAnglePicker(null);
    setGenerating(true);
    try {
      const res = await api.post(
        "/users/me/generate-before-after",
        {
          old_path: oldPath,
          new_path: newPath,
          old_date: oldDate,
          new_date: newDate,
          mode: "classic",
          ...(studentId ? { student_id: studentId } : {}),
        },
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const now = new Date();
      const pad = (n, w = 2) => n.toString().padStart(w, "0");
      const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      link.href = url;
      link.download = `evolucao_${angle}_${ts}.png`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = "Não foi possível gerar a imagem. Verifique se as fotos existem no servidor.";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const data = JSON.parse(text);
          if (data?.detail) message = data.detail;
        } catch (_) {}
      } else if (err?.response?.data?.detail) {
        message = typeof err.response.data.detail === "string"
          ? err.response.data.detail
          : message;
      }
      toast.error(message, 5000);
    } finally {
      setGenerating(false);
    }
  };



  return (
    <>
      {/* Toolbar */}
      <div className={styles.embeddedToolbar}>
        {!showForm && (
          <>
            {entries && entries.filter(e =>
              e.photo_front_url || e.photo_back_url || e.photo_right_url || e.photo_left_url
            ).length >= 2 && (
              <button
                className={styles.btnHeader}
                disabled={generating}
                onClick={() => generateBeforeAfter()}
                title="Antes e depois"
              >
                <FontAwesomeIcon icon={faScaleBalanced} />
                <span className={styles.btnLabelFull}>{generating ? " Gerando…" : " Antes e depois"}</span><span className={styles.btnLabelShort}>{generating ? "…" : " Comparar"}</span>
              </button>
            )}
            {entries && entries.filter(e =>
              e.photo_front_url || e.photo_back_url || e.photo_right_url || e.photo_left_url
            ).length >= 2 && (
              <button
                className={styles.btnHeader}
                disabled={analyzing}
                onClick={() => setShowAnalyzeConfirm(true)}
                title="Analisar com IA"
              >
                <FontAwesomeIcon icon={analyzing ? faSpinner : faBrain} spin={analyzing} />
                <span className={styles.btnLabelFull}>{analyzing ? " Analisando…" : " Analisar com IA"}</span><span className={styles.btnLabelShort}>{analyzing ? "…" : " Analisar IA"}</span>
              </button>
            )}
            {evolutionAnalysis && (
              <button
                className={styles.btnHeader}
                onClick={toggleHistory}
                title="Histórico de análises"
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
                <span className={styles.btnLabelFull}> Gerações IA</span><span className={styles.btnLabelShort}> Histórico</span>
              </button>
            )}
            {!readOnly && (
              <button className={styles.btnHeader} onClick={() => { resetForm(); setShowForm(true); }}>
                + Novo registro
              </button>
            )}

          </>
        )}
      </div>

      {/* Angle picker */}
      {anglePicker && !showForm && (
        <div className={styles.anglePicker}>
          <span className={styles.anglePickerLabel}>Qual ângulo?</span>
          <div className={styles.anglePickerBtns}>
            {anglePicker.angles.map(a => (
              <button
                key={a.key}
                className={styles.btnHeader}
                onClick={() => generateBeforeAfter(a.key)}
              >
                {a.label}
              </button>
            ))}
            <button
              className={styles.anglePickerCancel}
              onClick={() => setAnglePicker(null)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Analyzing overlay */}
      {analyzing && (
        <div className={styles.analyzingOverlay}>
          <div className={styles.analyzingSpinner}>
            <FontAwesomeIcon icon={faBrain} className={styles.analyzingIcon} />
          </div>
          <p className={styles.analyzingTitle}>Analisando evolução com IA…</p>
          <p className={styles.analyzingSubtitle}>Comparando fotos e gerando análise detalhada</p>
        </div>
      )}

      {/* Confirmation panel before AI analysis */}
      {showAnalyzeConfirm && !showForm && (
        <div className={styles.analyzeConfirmPanel}>
          <p className={styles.analyzeConfirmTitle}>
            <FontAwesomeIcon icon={faBrain} /> Análise de evolução com IA
          </p>
          <ul className={styles.analyzeConfirmList}>
            <li><FontAwesomeIcon icon={faLock} /><span>As fotos utilizadas ficam <strong>bloqueadas para exclusão</strong> para fins de auditoria</span></li>
            <li><FontAwesomeIcon icon={faArrowsRotate} /><span>Pode ser gerada <strong>1 vez a cada 7 dias</strong>, ou antes se novas fotos forem enviadas</span></li>
            <li><FontAwesomeIcon icon={faChartBar} /><span>Limite de <strong>4 gerações por mês</strong></span></li>
          </ul>
          <div className={styles.analyzeConfirmActions}>
            <button className={styles.btnCancel} onClick={() => setShowAnalyzeConfirm(false)}>Cancelar</button>
            <button
              className={styles.btnConfirmAnalyze}
              style={{ background: getCssVar("--accent", "#1d9e75"), color: "#fff" }}
              onClick={() => { setShowAnalyzeConfirm(false); handleAnalyzeEvolution(); }}
            >
              <FontAwesomeIcon icon={faBrain} /> Confirmar análise
            </button>
          </div>
        </div>
      )}

      {/* Analysis history panel */}
      {showHistory && !showForm && (
        <div className={styles.historyPanel}>
          <div className={styles.historyPanelHeader}>
            <FontAwesomeIcon icon={faClockRotateLeft} />
            <span>Histórico de análises IA</span>
            <button className={styles.historyClose} onClick={() => setShowHistory(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          {analysisHistory === null
            ? <p className={styles.historyLoading}>Carregando…</p>
            : analysisHistory.length === 0
              ? <p className={styles.historyEmpty}>Nenhuma análise gerada ainda.</p>
              : analysisHistory.map((a, idx) => (
                  <div key={a.id} className={styles.historyCard}>
                    <button
                      className={styles.historyCardHeader}
                      onClick={() => setExpandedAnalysis(expandedAnalysis === a.id ? null : a.id)}
                    >
                      <span className={styles.historyCardDate}>
                        {new Date(a.generated_at).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", year:"numeric" })}
                        {idx === 0 && <span className={styles.historyLatestBadge}>mais recente</span>}
                      </span>
                      {a.angles_analyzed && (
                        <span className={styles.historyCardAngles}>
                          {a.angles_analyzed.split(",").map(k =>
                            ({front:"Frente",back:"Costas",right:"Dir.",left:"Esq."})[k]||k
                          ).join(" · ")}
                        </span>
                      )}
                      <FontAwesomeIcon icon={expandedAnalysis === a.id ? faChevronUp : faChevronDown} />
                    </button>
                    {expandedAnalysis === a.id && (
                      <div className={styles.historyCardBody}>{a.analysis_text}</div>
                    )}
                  </div>
                ))
          }
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className={styles.form}>
          <p className={styles.formTitle}>{editEntry ? "Editar registro" : "Novo registro de progresso"}</p>

          <div className={styles.weightRow}>
            <label className={styles.label}>Data</label>
            <SplitDateInput
              value={recordedAt}
              onChange={setRecordedAt}
              inputClassName={styles.input}
            />
          </div>

          <div className={styles.weightRow}>
            <label className={styles.label}>Peso (kg) *</label>
            <input
              type="number"
              step="0.1"
              min="1"
              max="300"
              placeholder="ex: 75.5"
              className={styles.input}
              value={weight}
              onChange={e => setWeight(e.target.value)}
            />
          </div>

          <div>
            <label className={styles.label} style={{ marginBottom: 10, display: "block" }}>
              Fotos (mínimo 1)
            </label>
            <div className={styles.photoGrid}>
              {ANGLES.map(({ key, label }) => {
                const existingUrl = editExistingPhotos?.[key];
                const hasStagedNew = !!previews[key];
                const showExisting = !hasStagedNew && !!existingUrl;
                return (
                  <div key={key} className={styles.photoSlot}>
                    <span className={styles.photoSlotLabel}>{label}</span>
                    <div
                      className={styles.photoUpload}
                      onClick={() => fileRefs[key].current?.click()}
                    >
                      <input
                        ref={fileRefs[key]}
                        type="file"
                        accept="image/*"
                        onChange={e => handlePhoto(key, e.target.files?.[0])}
                      />
                      {hasStagedNew
                        ? <>
                            <img src={previews[key]} alt={label} className={styles.photoPreview} loading="lazy" />
                            <button
                              className={styles.photoRemove}
                              onClick={e => { e.stopPropagation(); removePhoto(key); }}
                            ><FontAwesomeIcon icon={faXmark} /></button>
                          </>
                        : showExisting
                          ? <>
                              <img src={buildSrc(existingUrl, apiBase)} alt={label} className={styles.photoPreview} loading="lazy" />
                              <span className={styles.photoReplaceHint}><FontAwesomeIcon icon={faCamera} /> Substituir</span>
                              {!isProtected && (
                                <button
                                  className={styles.photoDeleteBtn}
                                  title="Excluir foto"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setEditExistingPhotos(prev => ({ ...prev, [key]: null }));
                                    setDeletePhotos(prev => ({ ...prev, [key]: true }));
                                  }}
                                ><FontAwesomeIcon icon={faXmark} /></button>
                              )}
                            </>
                          : <>
                              <span className={styles.photoUploadIcon}><FontAwesomeIcon icon={faCamera} /></span>
                              <span className={styles.photoUploadHint}>Toque para adicionar</span>
                            </>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {error && <p style={{ color: "var(--feedback-error)", fontSize: 13, margin: 0 }}>{error}</p>}

          <div className={styles.formActions}>
            <button className={styles.btnCancel} onClick={() => { setShowForm(false); resetForm(); }}>
              Cancelar
            </button>
            <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
              {saving ? "Salvando…" : editEntry ? "Salvar alterações" : "Salvar registro"}
            </button>
          </div>
        </div>
      )}

      {/* History */}
      {!showForm && (
        loading
          ? <p style={{ color: "var(--muted)", textAlign: "center", padding: 32 }}>Carregando…</p>
          : !entries?.length
            ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}><FontAwesomeIcon icon={faCamera} /></div>
                <p>Nenhum registro ainda.<br />Clique em "+ Novo registro" para começar.</p>
                <button
                  type="button"
                  onClick={() => setPhotoGuideOpen(true)}
                  className={styles.photoGuideLink}
                >
                  <FontAwesomeIcon icon={faCamera} /> Ver guia de como tirar as fotos
                </button>
                <PhotoGuideModal open={photoGuideOpen} onClose={() => setPhotoGuideOpen(false)} imageUrl={photoGuideUrl} />
              </div>
            )
            : entries.map(entry => (
              <AlbumCard
                key={entry.id}
                entry={entry}
                apiBase={apiBase}
                onClick={() => setLightboxEntry(entry)}
                onDelete={setDeleteTarget}
                onEdit={!readOnly ? startEdit : null}
                isProtected={entryIsProtected(entry)}
              />
            ))
      )}



      {/* Lightbox */}
      {lightboxEntry && (
        <Lightbox
          entry={lightboxEntry}
          apiBase={apiBase}
          onClose={() => setLightboxEntry(null)}
        />
      )}

      {/* Confirm delete */}
      {deleteTarget && (
        <div className={styles.confirmOverlay} onClick={() => { setDeleteTarget(null); setDeleteError(""); }}>
          <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
            <p>Excluir o registro de <strong>{formatDate(deleteTarget.recorded_at)}</strong>?<br />
               As fotos serão removidas permanentemente.</p>
            {deleteError && <p style={{ color: "var(--feedback-error)", fontSize: 13 }}>{deleteError}</p>}
            <div className={styles.confirmActions}>
              <button className={styles.btnCancel} onClick={() => { setDeleteTarget(null); setDeleteError(""); }}>Cancelar</button>
              <button className={styles.btnConfirmDelete} onClick={confirmDelete}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Standalone modal (com suporte a studentId para visão do professor) ────────
export default function BodyProgressModal({ open, onClose, apiBase = "", studentId = null }) {
  const analyzingRef = useRef(false);
  const handleClose = useCallback(() => {
    if (analyzingRef.current) return;
    onClose();
  }, [onClose]);

  if (!open) return null;
  const title = studentId ? "Fotos de Progresso do Aluno" : "Progresso Corporal";
  return createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={handleClose} />
        <div className={styles.header}>
          <h2><FontAwesomeIcon icon={faCamera} /> {title}</h2>
        </div>
        <div className={styles.body}>
          <BodyProgressContent apiBase={apiBase} studentId={studentId} onAnalyzingChange={v => { analyzingRef.current = v; }} />
        </div>
      </div>
    </div>,
    document.body
  );
}